import React, { useEffect, useState } from "react";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { Box, Stack, Theme } from "@mui/material";
import { FilterModal } from "../FilterModal";
import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { ClientResponse, IQueryPerams, UserQuery } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import CustomizedSwitch from "../../components/Switch";
import { formatSnakeCaseToReadable } from "../../utils/common";
import { UserCategorySelectInput } from "../select-input-models/UserCategorySelectInput";
import { Field } from "../../utils/formSchema";
import { UserRoleSelectInput } from "../select-input-models/UserRoleSelectInput";

interface FilterProps<T> {
  ftn: (params: IQueryPerams) => Promise<ClientResponse<T>>;
  updateQuerySet: (query: React.SetStateAction<IQueryPerams>) => void;
  updateDataSet: (data: T) => void;
}

type USerQueryKey = keyof UserQuery;

export const UsersFilter = <T,>({
  ftn,
  updateQuerySet,
  updateDataSet,
}: FilterProps<T>) => {
  const [loading, setLoading] = useState(true);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<UserQuery>({});
  const resetFilter = <K extends keyof UserQuery>(key: K) => {
    updateForm((p) => {
      const value= typeof (p[key]?.value)=== 'boolean' ? false :""
      const state = {
        ...p,
        [key]: { value},
      };
      updateQuerySet((prev) => ({ ...prev, [key]: value }))
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    updateQuerySet(data);
    const filterRequest = async () => {
      setLoading(true);
      const res = await ftn(data);
      if (res.data && updateDataSet) {
        updateDataSet(res.data);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.telephone?.value,
    form.is_deleted?.value,
    form.email?.value,
    form.user_category_id?.value,
    form.role_id?.value
  ]);

  function handleCategorySelect(item: Field) {
    updateForm((p) => ({ ...p, user_category_id: item }));
  }
  function handleUserRoleOnchange(item: Field, roleName?: string) {
    updateForm((p) => ({
      ...p,
      role_id: item,
    }));
  }
  return (
    <Stack direction={"row"} justifyContent={"space-between"} width={"100%"}>
      <Stack direction={"row"} spacing={2}>
        <FilterModal label="Filters" loading={loading}>
          <Stack direction={"column"} spacing={2}>
            <SideBarSearchInput
              label="Telephone"
              placeholder="Search by telephone line ..."
              value={form.telephone?.value}
              handleChange={updateInputField("telephone")}
            />
            <UserCategorySelectInput
              label="User Category"
              placeholder="User Category"
              handleChange={handleCategorySelect}
              value={form.user_category_id?.value}
            />

            <UserRoleSelectInput
              placeholder="User Role"
              handleChange={handleUserRoleOnchange}
              label="User Role"
              value={form.role_id?.value}
            />

            <CustomizedSwitch
              title="deleted"
              onChange={updateInputField("is_deleted", "BOOLEAN")}
              value={form.is_deleted?.value ?? false}
            />
          </Stack>
        </FilterModal>
        <Box sx={styles.filterLayout}>
          {Object.entries(getFormValues())
            .filter(
              ([_, value]) =>
                (typeof value === "string" && value.trim() !== "") ||
                typeof value === "boolean"
            )
            .map(([key, value]) => (
              <FilterItemButton
                key={key}
                label={`By ${formatSnakeCaseToReadable(key)}`}
                tip={value.toString()}
                height={40}
                onClick={() => resetFilter(key as USerQueryKey)}
              />
            ))}
        </Box>
      </Stack>
      <Box width={250}>
        <SideBarSearchInput
          placeholder="Search by user email..."
          value={form.email?.value}
          handleChange={updateInputField("email")}
        />
      </Box>
    </Stack>
  );
};

const styles = {
  filterLayout: (theme: Theme) => ({
    width: 1000,
    [theme.breakpoints.down("xl")]: {
      width: 570,
    },
  }),
};
